import { useCallback, useEffect, useState } from 'react';
import AppConfig from '../AppConfig';
import {
  fetchOneDriveSelectionAsFiles,
  loadOneDrivePicker,
  openPicker as openOneDrivePicker
} from '../utils/cloudProviders/oneDriveClient';
import { validateFilesForUpload } from '../utils/fileValidation';
import { toaster } from '../utils/toast';

const useOneDriveImport = ({ onFilesReady, accept, useValidation = false }) => {
  const [isReady, setIsReady] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [fileErrorList, setFileErrorList] = useState([]);
  const isConfigured = Boolean(AppConfig.OneDriveClientId);

  useEffect(() => {
    if (!isConfigured) return;

    let isMounted = true;
    loadOneDrivePicker()
      .then(({ isSupported }) => {
        if (isMounted) setIsReady(isSupported);
      })
      .catch(() => {
        if (isMounted) setIsReady(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isConfigured]);

  const openPicker = useCallback(async () => {
    setIsImporting(true);

    try {
      const selection = await openOneDrivePicker({ multiselect: true, allowFolders: true });

      if (!selection?.length) return;

      const files = await fetchOneDriveSelectionAsFiles(selection);

      if (files.length === 0) {
        toaster({ message: 'Nothing could be imported from that selection', success: false });
        return;
      }

      if (!useValidation) {
        onFilesReady({ files });
        return;
      }

      const { validFiles, errorList } = await validateFilesForUpload({ files, accept });

      if (errorList.length) setFileErrorList(errorList);

      if (validFiles.length > 0) onFilesReady({ files: validFiles });
    } catch (error) {
      toaster({ message: 'Could not import from OneDrive', success: false });
      console.error('Error importing OneDrive files:', error);
    } finally {
      setIsImporting(false);
    }
  }, [onFilesReady, accept, useValidation]);

  return {
    isEnabled: isConfigured,
    isReady,
    isImporting,
    openPicker,
    fileErrorList,
    setFileErrorList
  };
};

export default useOneDriveImport;
